import { QuestionType } from "../models/enums/QuestionType";
import { TabBackground } from "../models/DynamicCssModel";
import type { Theme } from "../types/Theme";
import { backgroundColor, brighten, isDark } from "./Mixins";
import { shouldUseModernTheme } from "./navigationButtonThemeColors";

/** Horizontal border between modern grid table rows. */
export const MODERN_GRID_ROW_BORDER_COLOR = "#d1d1d1";

/** Zebra fill for even-position modern grid data rows; deliberately independent of {@link Theme.ControlAlpha} — legacy's equivalent (`TableRowDefaultTabFillSecondaryColor`) never reads it either. */
export const MODERN_GRID_EVEN_ROW_BACKGROUND = "rgb(138 138 138 / 6%)";

/** Hover fill for modern grid data rows. */
export const MODERN_GRID_ROW_HOVER_BACKGROUND = "rgb(138 138 138 / 20%)";

/** Muted sub-header text; matches palette @grey-600. */
export const MODERN_GRID_SUBHEADER_MUTED = "#757575";

/** Cell padding for modern HybridGrid table cells (px). */
export const MODERN_HYBRID_GRID_CELL_PADDING_PX = 8;

/**
 * Ceiling for modern grid row and wrapper corners. Legacy feeds the same {@link Theme.BorderRadius}
 * through `Mixins.getRowCornerBorderRadius`, whose log curve tops out at 25 — so this reproduces
 * legacy's effective maximum without adopting the curve itself, which would move the 8px default to
 * 11. Without a ceiling, Field Settings → Corners = Rounded (100) rounds the collapsible grid wrapper
 * so hard that its `overflow: hidden` clips the content.
 */
export const MODERN_GRID_MAX_ROW_RADIUS_PX = 25;

const MODERN_GRID_TABLE_QUESTION_TYPES = new Set<QuestionType>([
    QuestionType.HybridGrid,
    QuestionType.ChoiceGrid,
    QuestionType.ImageChoiceGrid,
]);

export function useModernGridTableStyles(theme: Theme): boolean {
    return shouldUseModernTheme(theme);
}

export function isModernGridTableQuestion(questionType: QuestionType): boolean {
    return MODERN_GRID_TABLE_QUESTION_TYPES.has(questionType);
}

/** Legacy grid dynamic CSS scope; excludes star rating row styling under modern theme. */
export function getLegacyGridDynamicCssScope(theme: Theme): string {
    const base = ".grid-view:not(.grid-table--modern)";
    return useModernGridTableStyles(theme)
        ? `${base}:not(.grid-view--star-rating)`
        : base;
}

export function resolveModernGridRowBorderColor(): string {
    return MODERN_GRID_ROW_BORDER_COLOR;
}

export function resolveModernGridEvenRowBackground(): string {
    return MODERN_GRID_EVEN_ROW_BACKGROUND;
}

/** Modern grid row / wrapper corner radius — Field Settings → Corners, capped per {@link MODERN_GRID_MAX_ROW_RADIUS_PX}. */
export function resolveModernGridRowBorderRadius(theme: Theme): number {
    return Math.min(theme.BorderRadius, MODERN_GRID_MAX_ROW_RADIUS_PX);
}

export function resolveModernGridRowHoverBackground(): string {
    return MODERN_GRID_ROW_HOVER_BACKGROUND;
}

/** Sub-header label color; lightens on dark page backgrounds for contrast. */
export function resolveModernGridSubHeaderColor(theme: Theme): string {
    const bg = backgroundColor(theme);
    // isDark() is true for light backgrounds in this codebase (YIQ luminance check).
    return isDark(bg) ? MODERN_GRID_SUBHEADER_MUTED : brighten(MODERN_GRID_SUBHEADER_MUTED, 2);
}

export function shouldSuppressModernHybridGridInputWrapperMargin(
    theme: Theme,
    inHybridGrid: boolean,
): boolean {
    return inHybridGrid && useModernGridTableStyles(theme);
}

export function isModernHybridGridTable(
    questionType: QuestionType | undefined,
    theme: Theme,
): boolean {
    return questionType === QuestionType.HybridGrid && useModernGridTableStyles(theme);
}

/** Whether DynamicCss row fill uses duoChrome (null-safe, case-insensitive). */
export function isDuoChromeTabBackground(
    tabBackground: TabBackground | string | null | undefined,
): boolean {
    if (tabBackground == null) {
        return false;
    }
    const normalized = String(tabBackground).toLowerCase();
    return normalized === TabBackground.DuoChrome.toLowerCase();
}

/** Modern grid zebra striping follows DynamicCss duoChrome row fill (same as legacy dynamic CSS). */
export function isModernGridDuoChromeStriping(theme: Theme): boolean {
    return isDuoChromeTabBackground(theme.DynamicCss?.TableRowDefaultTabBackground);
}
